import type { ComponentType, SVGProps } from 'react';
import LayoutDashboard from 'lucide-react/dist/esm/icons/layout-dashboard.js';
import FolderOpen from 'lucide-react/dist/esm/icons/folder-open.js';
import ListChecks from 'lucide-react/dist/esm/icons/list-checks.js';
import FilePlus from 'lucide-react/dist/esm/icons/file-plus.js';
import UserCog from 'lucide-react/dist/esm/icons/user-cog.js';
import CalendarClock from 'lucide-react/dist/esm/icons/calendar-clock.js';
import DoorOpen from 'lucide-react/dist/esm/icons/door-open.js';
import UserCheck from 'lucide-react/dist/esm/icons/user-check.js';
import Users from 'lucide-react/dist/esm/icons/users.js';
import Bell from 'lucide-react/dist/esm/icons/bell.js';

/**
 * Internal-portal route paths, in one place, and the role-filtered nav menu
 * (`DESIGN_SYSTEM.md` §5: "a role never sees a nav item it has no permission for ... a
 * visibility rule, not merely a disabled state").
 *
 * Gated on `permissions` from `/auth/me`, never on `roleCode` — CLAUDE.md's brief:
 * "A user's permissions are the server's answer; re-deriving them from a role name will
 * drift." `file.read` is the one permission every file-visibility screen in this build
 * shares (I6, I8, I10 all declare `access: protectedBy('file.read')` on the server —
 * confirmed by reading `file-routes.ts`/`workflow-routes.ts`), so it is also the one this
 * nav filters on. There is no finer-grained permission the server exposes to distinguish
 * "sees the register" from "has a personal task queue" — SEC/JS/US/HR/AUD/SA will all see
 * a "My Tasks" nav item whose list is legitimately empty for them (I10's own spec: an
 * empty task queue is a reassuring state, not a broken one), which is the honest
 * consequence of the permission model rather than an invented distinction this client
 * cannot verify. Flagged as a finding in the handback.
 *
 * **R-052 — that paragraph was wrong, and the distinction it says does not exist does.**
 * A task is a file you currently HOLD. Holding one means participating in movement, and
 * `file.move` ("Forward / Approve / Reject / Comment") is `D` — denied outright — for
 * `super_admin`, `admin_hr` and `auditor`, while the five roles that carry files all hold
 * it at `own`. So My Tasks gates on `file.move`, not `file.read`: an administrator's queue
 * was not "legitimately empty", it was **unreachable by construction**, and a menu item
 * that can never contain anything is not a reassuring empty state — it is a permanent
 * dead end that makes a Super Admin wonder what they are failing to see. `file.read` stays
 * the gate for File Register, which those roles genuinely can use (metadata only).
 *
 * **Fixed in this pass — the "only 3 menu items" defect.** File Intake and Account
 * Settings existed as real screens reachable only from a button on another page
 * (File Register's "New file") or a dropdown menu (`AccountMenu`'s "Account settings"),
 * never from the primary nav itself — the one list of "everything this account can do" a
 * staff member actually scans. Both get a real entry now: File Intake gated on
 * `file.register` (the exact permission `POST /files` itself requires, confirmed by
 * reading `workflow-routes.ts`), Account Settings ungated (every authenticated staff
 * member owns exactly one account, regardless of role).
 *
 * **This pass closes the gap the previous one named**: I12 (My Availability), I13
 * (Availability Administration), I14 (Appointment Inbox), I15 (Entrance Verification) and
 * I11's full Notification Centre page now EXIST as real screens, so each gets the nav
 * entry the earlier build deliberately withheld — every one gated on the exact permission
 * the corresponding server route requires (`availability.self`, `availability.admin`,
 * `appointment.inbox`, `appointment.verify`, `notification.read` — all read live off
 * `/auth/me` for `officer`/`adminhr`, not assumed from the contract's prose). I18 (Audit
 * Log) and I3/I4's own nav-adjacent needs remain unmounted — I18 because `internal-api`
 * mounts no `audit` route at all yet (confirmed: no `transport/*.ts` file exists in
 * `api/src/modules/audit`, and `routes.ts` never imports the module), I3 because
 * `POST /auth/password/change` is still not mounted (confirmed live: `404 NOT_FOUND` even
 * with a valid session/CSRF pair) — neither would be anything but a dead or broken link.
 */
export const ROUTES = {
  dashboard: '/dashboard',
  files: '/files',
  fileNew: '/files/new',
  fileDetail: (fileId: string) => `/files/${fileId}`,
  tasks: '/tasks',
  account: '/account',
  appointments: '/appointments',
  appointmentDetail: (appointmentId: string) => `/appointments/${appointmentId}`,
  entranceVerification: '/appointments/verify',
  myAvailability: '/availability',
  availabilityAdmin: '/availability/admin',
  notifications: '/notifications',
} as const;

export interface NavItem {
  to: string;
  labelKey: string;
  /**
   * ux-review defect (chrome pass): nav items were text-only in a bilingual UI where
   * icons give a non-linguistic scanning anchor — useful precisely when the same
   * person switches between NE and EN. Decorative only (`aria-hidden`, see AppShell);
   * the text label remains the accessible name, never the icon alone.
   */
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Absent = always visible to any authenticated staff member (e.g. the dashboard itself). */
  permission?: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  { to: ROUTES.dashboard, labelKey: 'shell.dashboard', icon: LayoutDashboard },
  { to: ROUTES.files, labelKey: 'shell.nav.files', icon: FolderOpen, permission: 'file.read' },
  { to: ROUTES.fileNew, labelKey: 'shell.nav.fileNew', icon: FilePlus, permission: 'file.register' },
  { to: ROUTES.tasks, labelKey: 'shell.nav.tasks', icon: ListChecks, permission: 'file.move' },
  { to: ROUTES.appointments, labelKey: 'shell.nav.appointments', icon: CalendarClock, permission: 'appointment.inbox' },
  { to: ROUTES.entranceVerification, labelKey: 'shell.nav.entranceVerification', icon: DoorOpen, permission: 'appointment.verify' },
  { to: ROUTES.myAvailability, labelKey: 'shell.nav.myAvailability', icon: UserCheck, permission: 'availability.self' },
  { to: ROUTES.availabilityAdmin, labelKey: 'shell.nav.availabilityAdmin', icon: Users, permission: 'availability.admin' },
  { to: ROUTES.notifications, labelKey: 'shell.nav.notifications', icon: Bell, permission: 'notification.read' },
  { to: ROUTES.account, labelKey: 'shell.nav.account', icon: UserCog },
];

/**
 * Should this nav link highlight only on an EXACT path match?
 *
 * Two opposite defects meet here. Without an exact match `/files` prefix-matches
 * `/files/new`, and File Register stayed lit while the user was on File Intake — two rows
 * lit at once is a navigation aid that misinforms. With an exact match ALWAYS, a detail
 * route that is not itself a nav item (`/admin/users/:id`) lights nothing, and the
 * sidebar tells the user they are nowhere.
 *
 * The rule that satisfies both: demand an exact match only where another nav item
 * actually lives underneath this one, which is the only arrangement that can light two
 * rows. `/files`, `/appointments` and `/availability` each have a child in the menu;
 * `/admin/users` does not, so its detail and sessions pages keep it lit.
 */
export function requiresExactMatch(to: string, items: readonly { to: string }[]): boolean {
  return items.some((other) => other.to !== to && other.to.startsWith(`${to}/`));
}

export function visibleNavItems(permissions: readonly string[]): NavItem[] {
  return NAV_ITEMS.filter((item) => item.permission === undefined || permissions.includes(item.permission));
}
